import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, FileText, Download, Trash2, ScanText, Sparkles } from "lucide-react";
import { api, mediaUrl } from "../../lib/api";

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function FileRow({ f, onDelete }) {
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2 border border-navy/10 rounded-lg text-sm" data-testid={`doc-file-${f.id}`}>
      <div className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-navy/40 shrink-0" /><span className="truncate text-navy/80">{f.name}</span><span className="text-navy/30 text-xs shrink-0">{kb(f.size || 0)}</span></div>
      <div className="flex items-center gap-2 shrink-0">
        <a href={mediaUrl(f.url)} target="_blank" rel="noreferrer" className="text-violet hover:text-navy" title="Descargar" data-testid={`doc-download-${f.id}`}><Download className="w-4 h-4" /></a>
        {onDelete && <button onClick={() => onDelete(f.id)} className="text-navy/30 hover:text-destructive" title="Eliminar"><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}

export default function ModuleDocs({ moduleKey, item, cfg, onRefresh }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [ocr, setOcr] = useState(item.texto_extraido ? { texto: item.texto_extraido, datos: item.datos, resumen: item.resumen } : null);
  const [placeholders, setPlaceholders] = useState([]);
  const [values, setValues] = useState({});
  const [generated, setGenerated] = useState(null);

  useEffect(() => {
    if (cfg.template) api.get(`/mod/templates/items/${item.id}/placeholders`).then((r) => setPlaceholders(r.data.placeholders || [])).catch(() => {});
  }, [cfg.template, item.id]);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      if (cfg.ocr) {
        const { data } = await api.post(`/mod/ocr/items/${item.id}/extract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setOcr({ texto: data.texto, datos: data.datos, resumen: data.resumen });
        toast.success("Documento digitalizado con IA");
      } else {
        await api.post(`/mod/${moduleKey}/items/${item.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Archivo subido");
      }
      onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al subir"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const delFile = async (fid) => {
    try { await api.delete(`/mod/${moduleKey}/items/${item.id}/file/${fid}`); onRefresh?.(); } catch { toast.error("Error"); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/mod/templates/items/${item.id}/generate`, { values });
      setGenerated(data.contenido);
      toast.success("Documento generado");
      onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al generar"); }
    finally { setBusy(false); }
  };

  const files = item.files || [];
  const generados = item.generados || [];

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-4" data-testid="module-docs">
      <input ref={fileRef} type="file" className="hidden" onChange={onFile}
        accept={cfg.ocr ? "image/*,application/pdf" : undefined} data-testid="doc-file-input" />

      {/* Subir / Digitalizar */}
      {(cfg.files || cfg.ocr) && (
        <div className="space-y-2">
          <div className="overline text-navy/40">{cfg.ocr ? "Digitalizar documento" : "Archivos"}</div>
          {cfg.filesHint && !cfg.ocr && <p className="text-xs text-navy/45 -mt-1" data-testid="doc-files-hint">{cfg.filesHint}</p>}
          <button onClick={pick} disabled={busy} className="btn btn-primary" data-testid="doc-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : cfg.ocr ? <ScanText className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
            {cfg.ocr ? "Subir imagen o PDF y extraer" : "Subir archivo"}
          </button>
          {files.length > 0 && <div className="space-y-1.5">{files.map((f) => <FileRow key={f.id} f={f} onDelete={cfg.files ? delFile : null} />)}</div>}
        </div>
      )}

      {/* Resultado OCR */}
      {cfg.ocr && ocr && (
        <div className="space-y-2" data-testid="ocr-result">
          {ocr.resumen && <div className="text-sm text-navy/70"><span className="overline text-navy/40 block mb-1">Resumen IA</span>{ocr.resumen}</div>}
          <div className="overline text-navy/40">Texto extraído</div>
          <pre className="text-xs text-navy/70 bg-navy/[0.03] rounded-lg p-3 whitespace-pre-wrap max-h-64 overflow-y-auto">{ocr.texto || "—"}</pre>
          {ocr.datos && Object.values(ocr.datos).some((v) => Array.isArray(v) && v.length) && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(ocr.datos).flatMap(([k, arr]) => (Array.isArray(arr) ? arr : []).map((v, i) => (
                <span key={`${k}-${i}`} className="badge badge-muted text-xs">{k}: {String(v)}</span>
              )))}
            </div>
          )}
        </div>
      )}

      {/* Generar documento desde plantilla */}
      {cfg.template && (
        <div className="space-y-3" data-testid="template-generator">
          <div className="overline text-navy/40">Generar documento</div>
          {placeholders.length === 0 ? (
            <p className="text-sm text-navy/40">Escribe el contenido de la plantilla con campos como <code className="text-violet">{"{{cliente}}"}</code> y guárdala; aquí podrás llenarlos y generar el documento.</p>
          ) : (
            <>
              {placeholders.map((p) => (
                <div key={p}>
                  <label className="text-xs text-navy/50 mb-1 block">{p}</label>
                  <input className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm"
                    value={values[p] || ""} onChange={(e) => setValues({ ...values, [p]: e.target.value })} data-testid={`tpl-field-${p}`} />
                </div>
              ))}
              <button onClick={generate} disabled={busy} className="btn btn-primary" data-testid="tpl-generate-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar documento
              </button>
            </>
          )}
          {generated && <pre className="text-xs text-navy/70 bg-navy/[0.03] rounded-lg p-3 whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid="tpl-preview">{generated}</pre>}
          {generados.length > 0 && (
            <div className="space-y-1.5"><div className="overline text-navy/40">Documentos generados</div>{generados.map((f) => <FileRow key={f.id} f={f} />)}</div>
          )}
        </div>
      )}
    </div>
  );
}
